import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  PlusCircle, Pencil, Trash2, Receipt, CalendarClock, ChevronDown, ChevronUp, Loader2
} from "lucide-react";
import api from "@/api";

const FREQ_LABELS = { mensile: "Mensile", trimestrale: "Trimestrale", annuale: "Annuale", una_tantum: "Una Tantum" };
const PAY_LABELS = { RID: "RID", bonifico: "Bonifico", contanti: "Contanti", POS: "POS", altro: "Altro" };
const EMPTY_FORM = {
  frequency: "mensile", scheduledDay: 1, scheduledMonth: 1, scheduledDate: "",
  grossAmount: "", vatRate: 22, billingType: "fisso", paymentMethod: "bonifico",
  managementType: "agenzia", description: "", status: "attivo", notes: ""
};

export default function BillingPlanSection({ clientId, clientName, managementType }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM, managementType: managementType || "agenzia" });

  const loadPlans = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await api.getBillingPlansByClient(clientId);
      setPlans(data || []);
    } catch { setPlans([]); }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { if (expanded) loadPlans(); }, [expanded, loadPlans]);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, managementType: managementType || "agenzia" });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      frequency: p.frequency || "mensile",
      scheduledDay: p.scheduledDay || 1,
      scheduledMonth: p.scheduledMonth || 1,
      scheduledDate: p.scheduledDate || "",
      grossAmount: p.grossAmount || "",
      vatRate: p.vatRate ?? 22,
      billingType: p.billingType || "fisso",
      paymentMethod: p.paymentMethod || "bonifico",
      managementType: p.managementType || "agenzia",
      description: p.description || "",
      status: p.status || "attivo",
      notes: p.notes || ""
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      ...form,
      clientId,
      clientName,
      grossAmount: Number(form.grossAmount) || 0,
      vatRate: Number(form.vatRate) || 22,
      scheduledDay: Number(form.scheduledDay) || 1,
      scheduledMonth: form.frequency === "annuale" ? (Number(form.scheduledMonth) || 1) : null,
      scheduledDate: form.frequency === "una_tantum" ? form.scheduledDate : null,
    };
    try {
      if (editingId) {
        await api.updateBillingPlan(editingId, payload);
      } else {
        await api.createBillingPlan(payload);
      }
      setDialogOpen(false);
      loadPlans();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare questo piano di fatturazione?")) return;
    try { await api.deleteBillingPlan(id); loadPlans(); } catch (e) { console.error(e); }
  };

  const currency = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " \u20AC";

  return (
    <div className="mt-3">
      <button
        onClick={() => setExpanded(v => !v)}
        className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700 transition-colors w-full text-left"
        data-testid={`billing-plan-toggle-${clientId}`}
      >
        <Receipt className="h-4 w-4 text-blue-600" />
        Piano di Fatturazione ({plans.length})
        {expanded ? <ChevronUp className="h-3.5 w-3.5 ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 ml-auto" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2">
          {loading ? <p className="text-xs text-slate-400">Caricamento...</p> : (
            <>
              {plans.length === 0 ? (
                <p className="text-xs text-slate-400">Nessun piano configurato</p>
              ) : (
                <div className="space-y-1.5">
                  {plans.map(p => (
                    <div key={p.id} className="flex items-center gap-2 py-1.5 px-2.5 rounded bg-slate-50 border border-slate-100 text-xs" data-testid={`billing-plan-${p.id}`}>
                      <Badge variant="outline" className="text-[10px] shrink-0">{FREQ_LABELS[p.frequency] || p.frequency}</Badge>
                      <span className="font-medium text-slate-700">{currency(p.grossAmount)}</span>
                      <span className="text-slate-400 truncate flex-1">{p.description || "—"}</span>
                      <Badge variant="outline" className="text-[10px]">{PAY_LABELS[p.paymentMethod] || p.paymentMethod}</Badge>
                      <Badge className={p.status === "attivo" ? "text-[10px] bg-green-50 text-green-700 border-green-200" : "text-[10px] bg-slate-100 text-slate-500"}>
                        {p.status === "attivo" ? "Attivo" : "Inattivo"}
                      </Badge>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEdit(p)}><Pencil className="h-3 w-3 text-slate-400" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => handleDelete(p.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={openNew} data-testid={`add-billing-plan-${clientId}`}>
                <PlusCircle className="h-3.5 w-3.5 mr-1" />Nuovo Piano
              </Button>
            </>
          )}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-blue-600" />
              {editingId ? "Modifica Piano" : "Nuovo Piano di Fatturazione"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Frequenza *</Label>
                <Select value={form.frequency} onValueChange={v => setForm(f => ({ ...f, frequency: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensile">Mensile</SelectItem>
                    <SelectItem value="trimestrale">Trimestrale</SelectItem>
                    <SelectItem value="annuale">Annuale</SelectItem>
                    <SelectItem value="una_tantum">Una Tantum</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Gestione *</Label>
                <Select value={form.managementType} onValueChange={v => setForm(f => ({ ...f, managementType: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="agenzia">Agenzia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.frequency === "mensile" && (
              <div>
                <Label className="text-xs">Giorno del mese (1-28)</Label>
                <Input type="number" min={1} max={28} className="h-8 text-xs" value={form.scheduledDay}
                  onChange={e => setForm(f => ({ ...f, scheduledDay: e.target.value }))} />
              </div>
            )}
            {form.frequency === "trimestrale" && (
              <div>
                <Label className="text-xs">Giorno del mese per fatturazione trimestrale (1-28)</Label>
                <Input type="number" min={1} max={28} className="h-8 text-xs" value={form.scheduledDay}
                  onChange={e => setForm(f => ({ ...f, scheduledDay: e.target.value }))} />
              </div>
            )}
            {form.frequency === "annuale" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Mese</Label>
                  <Select value={String(form.scheduledMonth || 1)} onValueChange={v => setForm(f => ({ ...f, scheduledMonth: Number(v) }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Gennaio","Febbraio","Marzo","Aprile","Maggio","Giugno","Luglio","Agosto","Settembre","Ottobre","Novembre","Dicembre"].map((m, i) => (
                        <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Giorno (1-28)</Label>
                  <Input type="number" min={1} max={28} className="h-8 text-xs" value={form.scheduledDay}
                    onChange={e => setForm(f => ({ ...f, scheduledDay: e.target.value }))} />
                </div>
              </div>
            )}
            {form.frequency === "una_tantum" && (
              <div>
                <Label className="text-xs">Data prevista *</Label>
                <Input type="date" className="h-8 text-xs" value={form.scheduledDate || ""}
                  onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))} />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Importo Lordo (IVA inclusa) *</Label>
                <Input type="number" step="0.01" min="0" className="h-8 text-xs" value={form.grossAmount}
                  onChange={e => setForm(f => ({ ...f, grossAmount: e.target.value }))}
                  placeholder="0,00" />
              </div>
              <div>
                <Label className="text-xs">Aliquota IVA %</Label>
                <Input type="number" step="1" min="0" max="100" className="h-8 text-xs" value={form.vatRate}
                  onChange={e => setForm(f => ({ ...f, vatRate: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Tipo Fatturazione</Label>
                <Select value={form.billingType} onValueChange={v => setForm(f => ({ ...f, billingType: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fisso">Fisso</SelectItem>
                    <SelectItem value="a_prestazione">A Prestazione</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Metodo Pagamento *</Label>
                <Select value={form.paymentMethod} onValueChange={v => setForm(f => ({ ...f, paymentMethod: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RID">RID</SelectItem>
                    <SelectItem value="bonifico">Bonifico</SelectItem>
                    <SelectItem value="contanti">Contanti</SelectItem>
                    <SelectItem value="POS">POS</SelectItem>
                    <SelectItem value="altro">Altro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Descrizione servizio</Label>
              <Input className="h-8 text-xs" value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Es. Gestione paghe mensile" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Stato</Label>
                <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attivo">Attivo</SelectItem>
                    <SelectItem value="inattivo">Inattivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Note amministrative</Label>
              <Textarea className="text-xs" rows={2} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Note opzionali..." />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button size="sm" onClick={handleSave} disabled={!form.grossAmount || !form.paymentMethod}
              data-testid="save-billing-plan-btn">
              {editingId ? "Salva" : "Crea Piano"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
